import {OrderStatus} from '@wix/events-types'
import {
  GetFormattedMoney,
  getPreliminaryInvoice as calculatePreliminaryInvoice,
  getPricingOption,
  getPricingOptionLabel,
  getTaxConfig,
  getTicketDefinitionCurrency,
  hasSeatingPlan,
  isDonationTicketDefinition,
} from '@wix/wix-events-commons-statics'
import type {TFunction} from 'react-i18next'
import {isTemplate} from '../../../../commons/selectors/instance'
import {OrderedTicket, State} from '../types/state'
import {getDemoInvoice} from '../utils/checkout'
import {getBuyerDetailsOld} from './checkout'
import {getEvent} from './event'
import {getInvoice as getInvoiceFromState} from './invoice'
import {isEventPreview} from './navigation'
import {getBestPurchasedPlan, getSelectedPaidPlan} from './paid-plans'
import {getSeatsPreliminaryInvoiceItems} from './seating/places'
import {getPreliminaryInvoiceItems} from './selected-tickets'
import {getTickets as getTicketDefinitions} from './tickets'

export const getInvoice = (state: State): wix.events.ticketing.Invoice => {
  if (isTemplate(state) || isEventPreview(state)) {
    return getPreviewInvoice(state)
  }

  return getInvoiceFromState(state) ?? getPreliminaryInvoice(state)
}

export const getPreviewInvoice = (state: State) => {
  const tickets = getTicketDefinitions(state)

  return getDemoInvoice(tickets)
}

export const getPreliminaryInvoice = (state: State) => {
  const event = getEvent(state)
  const paidPlan = getSelectedPaidPlan(state) || getBestPurchasedPlan(state)

  const preliminaryInvoiceItems = hasSeatingPlan(event)
    ? getSeatsPreliminaryInvoiceItems(state)
    : getPreliminaryInvoiceItems(state)

  return calculatePreliminaryInvoice(
    getTicketDefinitions(state),
    preliminaryInvoiceItems,
    getTaxConfig(event) as wix.events.TaxConfig,
    paidPlan,
  )
}

export const isOrderCompleted = (order: wix.events.ticketing.Order) => Boolean(order?.snapshotId)

export const onlyFreeTicketsSelected = (order: wix.events.ticketing.Order) =>
  Number(order.invoice.grandTotal.amount) === 0

export const getPlacedOrder = (state: State) => state.placedOrder.order

export const getTickets = (state: State) => state.placedOrder.order.tickets ?? []

export const getTicketsPdf = (state: State) => state.placedOrder.order.ticketsPdf

export const getTicketQuantity = (state: State) => state.placedOrder.order.ticketsQuantity

export const getDownloadTicketsUrl = (state: State) => state.placedOrder.downloadTicketsUrl

export const getLoadingTicketsUrl = (state: State) => state.placedOrder.loadingTicketsUrl

export const hasPlacedOrder = (state: State) => Boolean(getPlacedOrder(state))

export const isPreparingOrder = (order: wix.events.ticketing.Order) => {
  return order?.tickets?.length !== order?.ticketsQuantity
}

export const isPollEnded = (state: State) => state.placedOrder.pollEnded

export const getGrandTotal = (state: State): number => Number(state.placedOrder.order.invoice.grandTotal.amount)

export const getPaymentMethod = (state: State) => state.placedOrder.order.method

export const getOrderNumber = (state: State) => state.placedOrder.order?.orderNumber

export const getNonFreeTicketsCount = (state: State) =>
  state.placedOrder.order?.invoice.items.reduce(
    (sum, item) => sum + ((Number(item.total.amount) && item.quantity) || 0),
    0,
  )

export const getSummaryTicketPrice = (ticket: OrderedTicket, t: TFunction, getFormattedMoney: GetFormattedMoney) => {
  const currency = getTicketDefinitionCurrency(ticket)
  let labeledPrice: string
  let price: number

  if (isDonationTicketDefinition(ticket)) {
    price = Number(ticket.priceOverride)
    labeledPrice = t('mobile.thanks.donation', {
      donation: getFormattedMoney({amount: ticket.priceOverride, currency}),
    })
  } else if (ticket.pricingOptionId) {
    price = Number(getPricingOption(ticket, ticket.pricingOptionId).price.amount)
    labeledPrice = getPricingOptionLabel(ticket, ticket.pricingOptionId, getFormattedMoney)
  } else {
    price = Number(ticket.price.amount)
    labeledPrice = t('mobile.thanks.price', {price: getFormattedMoney({amount: ticket.price.amount, currency})})
  }

  const total = getFormattedMoney({amount: (price * ticket.orderedCount).toString(), currency})

  return {price, labeledPrice, total}
}

export const getSummaryTicketPriceFromReservation = (
  ticket: wix.events.ticketing.TicketDefinition,
  t: TFunction,
  getFormattedMoney: GetFormattedMoney,
  quantity: number,
  ticketDetails?: wix.events.ticketing.TicketDetails,
) => {
  const currency = ticket.price.currency
  let price: number
  let labeledPrice: string

  if (!ticketDetails?.pricingOptionId && ticketDetails?.priceOverride) {
    price = Number(ticketDetails?.priceOverride)
    labeledPrice = t('mobile.thanks.donation', {
      donation: getFormattedMoney({amount: ticketDetails?.priceOverride, currency}),
    })
  } else if (ticketDetails?.pricingOptionName) {
    price = Number(ticketDetails?.priceOverride)
    labeledPrice = `${ticketDetails.pricingOptionName} ${getFormattedMoney({
      amount: ticketDetails?.priceOverride,
      currency,
    })}`
  } else {
    price = Number(ticket.price.amount)
    labeledPrice = t('mobile.thanks.price', {price: getFormattedMoney({amount: ticket.price.amount, currency})})
  }

  const total = getFormattedMoney({amount: (price * quantity).toString(), currency})

  return {price, labeledPrice, total}
}

export const getSummaryTicketName = (
  ticket: wix.events.ticketing.TicketDefinition,
  ticketDetails?: wix.events.ticketing.TicketDetails,
) => {
  return ticketDetails?.pricingOptionName ? `${ticket.name} (${ticketDetails.pricingOptionName})` : ticket.name
}

export const getSummaryPlaceInfo = (ticketDetails: wix.events.ticketing.TicketDetails) => {
  return {
    sectionTitle: ticketDetails.sectionLabel,
    elementTitle: ticketDetails.areaLabel || ticketDetails.tableLabel || ticketDetails.rowLabel,
    area: ticketDetails.areaLabel !== undefined,
    table: ticketDetails.tableLabel !== undefined,
    seatLabel: ticketDetails.seatLabel,
  }
}

export const getOrderForDemoFlow = (state: State): wix.events.ticketing.Order => {
  const buyerDetails = getBuyerDetailsOld(state)

  const tickets = Object.keys(state.selectedTickets).map(key => ({
    ticketDefinitionId: key,
    ...getTicketDefinitions(state).find(ticket => ticket.id === key),
  }))

  return {
    firstName: buyerDetails.firstName,
    email: buyerDetails.email,
    invoice: calculatePreliminaryInvoice(
      getTicketDefinitions(state),
      getPreliminaryInvoiceItems(state),
      getTaxConfig(getEvent(state)) as wix.events.TaxConfig,
    ),
    status: OrderStatus.FREE,
    tickets,
    ticketsQuantity: tickets.length,
  }
}

export const getSuccessfulOrder = (state: State): wix.events.ticketing.Order => {
  const template = isTemplate(state)
  const eventPreview = isEventPreview(state)

  if (template || eventPreview) {
    return getOrderForDemoFlow(state)
  }

  return getPlacedOrder(state)
}
